import { z } from 'zod';
import { workforceCommandIdSchema, workforceMembershipIdSchema } from './workforce-profile.contract.js';
export const workforceActivityRecordsCapability = 'workforce.activity-records.v1';
export const workforceActivitySectionSchema = z.enum(['attendance', 'behavior']);
export const workforceAttendanceCategorySchema = z.enum([
    'late-arrival',
    'left-early',
    'called-off-sick',
    'sick-day',
    'personal-day',
    'family-or-emergency',
    'no-call-no-show',
    'approved-time-off',
    'other-attendance',
]);
export const workforceBehaviorCategorySchema = z.enum([
    'positive-recognition',
    'coaching-conversation',
    'policy-violation',
    'safety-observation',
    'customer-feedback',
    'general-note',
]);
export const workforceActivityCategorySchema = z.union([
    workforceAttendanceCategorySchema,
    workforceBehaviorCategorySchema,
]);
export const workforceActivitySourceSchema = z.enum(['manual', 'assistant']);
export function workforceActivitySectionForCategory(category) {
    return workforceAttendanceCategorySchema.safeParse(category).success ? 'attendance' : 'behavior';
}
export const workforceActivityRecordInputSchema = z.strictObject({
    membershipId: workforceMembershipIdSchema,
    section: workforceActivitySectionSchema,
    category: workforceActivityCategorySchema,
    occurredAt: z.string().datetime({ offset: true }),
    note: z.string().trim().min(1).max(12_000),
    source: workforceActivitySourceSchema.default('manual'),
}).superRefine((value, context) => {
    if (workforceActivitySectionForCategory(value.category) !== value.section) {
        context.addIssue({
            code: 'custom',
            path: ['section'],
            message: 'The activity category does not belong to the selected section.',
        });
    }
});
export const workforceActivityRecordSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    membershipId: workforceMembershipIdSchema,
    displayName: z.string().trim().min(1).max(240),
    section: workforceActivitySectionSchema,
    category: workforceActivityCategorySchema,
    occurredAt: z.string().datetime({ offset: true }),
    note: z.string().trim().min(1).max(12_000),
    source: workforceActivitySourceSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: workforceMembershipIdSchema,
    updatedByMembershipId: workforceMembershipIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const workforceActivityRecordVersionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    recordId: z.string().uuid(),
    revision: z.number().int().positive(),
    operation: z.enum(['create', 'update', 'archive', 'restore']),
    record: workforceActivityRecordInputSchema,
    archivedAfter: z.boolean(),
    reason: z.string().trim().min(5).max(500),
    actorMembershipId: workforceMembershipIdSchema,
    createdAt: z.string().datetime({ offset: true }),
});
export const workforceActivityQuerySchema = z.strictObject({
    membershipId: workforceMembershipIdSchema.nullable().default(null),
    section: workforceActivitySectionSchema.nullable().default(null),
    category: workforceActivityCategorySchema.nullable().default(null),
    query: z.string().trim().max(160).default(''),
    occurredFrom: z.string().datetime({ offset: true }).nullable().default(null),
    occurredTo: z.string().datetime({ offset: true }).nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().min(0).default(0),
}).superRefine((value, context) => {
    if (value.occurredFrom !== null && value.occurredTo !== null &&
        Date.parse(value.occurredFrom) >= Date.parse(value.occurredTo)) {
        context.addIssue({ code: 'custom', path: ['occurredTo'], message: 'The end must follow the start.' });
    }
});
export const workforceCreateActivityCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: workforceCommandIdSchema,
    recordId: z.string().uuid(),
    record: workforceActivityRecordInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const workforceUpdateActivityCommandSchema = z.strictObject({
    kind: z.literal('update'),
    commandId: workforceCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    record: workforceActivityRecordInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const workforceActivityStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: workforceCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const workforceActivityCommandSchema = z.discriminatedUnion('kind', [
    workforceCreateActivityCommandSchema,
    workforceUpdateActivityCommandSchema,
    workforceActivityStateCommandSchema,
]);
const workforceActivityCommandMetadataSchema = z.strictObject({
    commandId: workforceCommandIdSchema,
    idempotent: z.boolean(),
});
export const workforceActivityRecordResponseSchema = z.strictObject({
    data: workforceActivityRecordSchema,
    command: workforceActivityCommandMetadataSchema.optional(),
});
export const workforceActivityRecordsResponseSchema = z.strictObject({
    data: z.array(workforceActivityRecordSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const workforceActivityRecordVersionsResponseSchema = z.strictObject({
    data: z.array(workforceActivityRecordVersionSchema),
});
//# sourceMappingURL=workforce-activity.contract.js.map